import { useState, useEffect } from 'react';
import { ChevronRight, Clock, Trophy, CheckCircle, XCircle, ArrowLeft, ArrowRight, RotateCcw, Share2, Star, Zap, Award, Menu, Brain, Sparkles, Crown, Lock, Gift, Rocket, TrendingUp, Users, GraduationCap, Briefcase } from 'lucide-react';
import { SelectedAnswers } from './data';
import { useLocalization } from './hooks/useLocalization';
import LanguageSelector from './components/LanguageSelector';
import { animateOnlyDesktop, isMobileDevice } from './utils/mobileUtils';
import { isFeatureEnabled } from './utils/accessCodes';

interface ISTQBAIDemoQuizProps {
  onBuyFullVersion?: () => void;
}

const ISTQBAIDemoQuiz = ({ onBuyFullVersion }: ISTQBAIDemoQuizProps = {}) => {
  // Localization hook
  const { currentLocale, changeLocale, uiTexts, demoQuestions } = useLocalization();
  
  // Отключаем анимации на мобильных устройствах при инициализации
  useEffect(() => {
    if (isMobileDevice()) {
      document.body.classList.add('mobile-no-animations');
    }
  }, []);
  
  const [currentView, setCurrentView] = useState<string>('welcome');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswers, setSelectedAnswers] = useState<SelectedAnswers>({});
  // Убрали lockedAnswers - теперь можно менять ответы
  const [timeLeft, setTimeLeft] = useState<number>(900); // 15 minutes for demo
  const [quizStarted, setQuizStarted] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [_score, setScore] = useState<number>(0); // Сохраняем для backward compatibility, префикс _ показывает что не используется в UI
  const [showDetailedResults, setShowDetailedResults] = useState<boolean>(false);
  const [_currentStreak, setCurrentStreak] = useState<number>(0); // Не используется в UI, только для расчетов
  const [maxStreak, setMaxStreak] = useState<number>(0);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Навигация клавишами влево-вправо (только для десктопа)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Проверяем что мы в режиме квиза и это десктоп
      if (!quizStarted || showResults || window.innerWidth < 768) return;
      
      if (event.key === 'ArrowLeft' && currentQuestion > 0) {
        setCurrentQuestion(currentQuestion - 1);
        event.preventDefault();
      } else if (event.key === 'ArrowRight' && currentQuestion < demoQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [quizStarted, showResults, currentQuestion, demoQuestions.length]);

  // Force re-render when locale changes to update UI texts and questions
  useEffect(() => {
    // This effect ensures the component re-renders when locale changes
    // to update all UI texts properly and questions in results view
    if (showResults && demoQuestions.length > 0) {
      // When showing results, we need to force a re-render to show 
      // questions in the new language in detailed results
      // The demoQuestions are already updated by the localization hook
    }
  }, [currentLocale, demoQuestions]);

  // Demo questions - 12 engaging questions from first 2 chapters
  // Now imported from external file: src/data/locales/en/demoQuestions.ts


  // Scroll to top on component mount and view changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentView, showResults]);

  // Timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (quizStarted && timeLeft > 0 && !showResults) {
      timer = setInterval(() => {
        setTimeLeft(time => {
          if (time <= 1) {
            handleFinishQuiz();
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [quizStarted, timeLeft, showResults]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Function to get explanation for specific answer choice
  const getExplanationForAnswer = (question: typeof demoQuestions[0], answerIndex: number): string => {
    switch (answerIndex) {
      case 0: return question.expA;
      case 1: return question.expB;
      case 2: return question.expC;
      case 3: return question.expD;
      default: return question.expA; // fallback to first explanation
    }
  };

  // Show upgrade modal for full review
  const showFullReviewUpgrade = () => {
    setShowUpgradeModal(true);
  };

  // Mobile haptic feedback simulation
  const triggerHapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light') => {
    if (navigator.vibrate) {
      const patterns = {
        light: 10,
        medium: 20,
        heavy: 50
      };
      navigator.vibrate(patterns[type]);
    }
  };

  const handleAnswerSelect = (questionId: number, answerIndex: number): void => {
    // Убираем блокировку ответов - теперь можно менять выбор
    
    // Haptic feedback - убираем зависимость от правильности ответа
    triggerHapticFeedback('light');
    
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));

    // Убираем блокировку и обновление streak во время прохождения
    // Streak будет рассчитываться только в результатах
  };

  // Touch handlers for swipe navigation with improved angle detection
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = touchStart.y - touchEnd.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Check if swipe distance is sufficient
    if (distance < minSwipeDistance) return;
    
    // Calculate angle of swipe (in degrees)
    const angle = Math.abs(Math.atan2(deltaY, deltaX) * 180 / Math.PI);
    
    // Only register horizontal swipes (angle between 0-30° or 150-180°)
    const isHorizontalSwipe = angle <= 30 || angle >= 150;
    
    // Additionally check that horizontal movement is at least 2x larger than vertical
    const isHorizontalDominant = Math.abs(deltaX) >= Math.abs(deltaY) * 2;
    
    if (isHorizontalSwipe && isHorizontalDominant) {
      const isLeftSwipe = deltaX > 0;
      const isRightSwipe = deltaX < 0;

      if (isLeftSwipe && currentQuestion < demoQuestions.length - 1) {
        handleNextQuestion();
      }
      if (isRightSwipe && currentQuestion > 0) {
        handlePrevQuestion();
      }
    }
  };

  const handleNextQuestion = (): void => {
    if (currentQuestion < demoQuestions.length - 1) {
      triggerHapticFeedback('light');
      setCurrentQuestion(prev => prev + 1);
      // Smooth scroll to top on mobile
      if (window.innerWidth < 768) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePrevQuestion = (): void => {
    if (currentQuestion > 0) {
      triggerHapticFeedback('light');
      setCurrentQuestion(prev => prev - 1);
      // Smooth scroll to top on mobile
      if (window.innerWidth < 768) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleFinishQuiz = (): void => {
    triggerHapticFeedback('heavy');
    let totalScore = 0;
    demoQuestions.forEach(question => {
      if (selectedAnswers[question.id] === question.correct) {
        totalScore += question.points;
      }
    });
    setScore(totalScore);
    
    // Рассчитываем maxStreak на основе финальных ответов
    const finalMaxStreak = calculateMaxStreak();
    setMaxStreak(finalMaxStreak);
    
    setShowResults(true);
    setQuizStarted(false);
    // Smooth scroll to top on mobile
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const setCurrentQuestionWithFeedback = (questionIndex: number): void => {
    triggerHapticFeedback('light');
    setCurrentQuestion(questionIndex);
    // Smooth scroll to top on mobile
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const startQuiz = (): void => {
    actuallyStartQuiz();
  };

  const actuallyStartQuiz = (): void => {
    triggerHapticFeedback('medium');
    setCurrentView('quiz');
    setQuizStarted(true);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimeLeft(900); // 15 minutes
    setShowResults(false);
    setShowDetailedResults(false);
    setCurrentStreak(0);
    setMaxStreak(0);
    setTouchStart(null);
    setTouchEnd(null);
    
    // Scroll to top when starting quiz
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // Prevent zoom on mobile
    if (window.innerWidth < 768) {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
      }
    }
  };


  const restartQuiz = (): void => {
    setCurrentView('welcome');
    setQuizStarted(false);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimeLeft(900);
    setShowResults(false);
    setScore(0);
    setShowDetailedResults(false);
    setCurrentStreak(0);
    setMaxStreak(0);
    setTouchStart(null);
    setTouchEnd(null);
    
    // Scroll to top when restarting quiz
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // 🔧 Подсчет количества правильных ответов
  const getCorrectAnswersCount = (): number => {
    return demoQuestions.filter(question => 
      selectedAnswers[question.id] === question.correct
    ).length;
  };

  // 🔧 Правильный подсчет процента на основе количества правильных ответов
  const getScorePercentage = (): number => {
    const correctCount = getCorrectAnswersCount();
    return Math.round((correctCount / demoQuestions.length) * 100);
  };

  // 🔧 Правильная проверка прохождения на основе процента правильных ответов
  const isPassed = (): boolean => {
    const correctCount = getCorrectAnswersCount();
    return correctCount >= Math.ceil(demoQuestions.length * 0.7); // 70% правильных ответов
  };

  // 🔧 Расчет максимального streak на основе финальных ответов
  const calculateMaxStreak = (): number => {
    let maxStreak = 0;
    let currentStreak = 0;
    
    demoQuestions.forEach(question => {
      const isCorrect = selectedAnswers[question.id] === question.correct;
      if (isCorrect) {
        currentStreak++;
        maxStreak = Math.max(maxStreak, currentStreak);
      } else {
        currentStreak = 0;
      }
    });
    
    return maxStreak;
  };

  const getPerformanceLevel = (): { level: string; emoji: string; message: string } => {
    const percentage = getScorePercentage();
    if (percentage >= 90) return { level: uiTexts.results.performance.genius, emoji: "🧠✨", message: uiTexts.results.congratulations };
    if (percentage >= 80) return { level: uiTexts.results.performance.expert, emoji: "🎯🔥", message: uiTexts.results.congratulations };
    if (percentage >= 70) return { level: uiTexts.results.performance.pro, emoji: "🚀💫", message: uiTexts.results.congratulations };
    if (percentage >= 60) return { level: uiTexts.results.performance.learner, emoji: "📚⚡", message: uiTexts.results.congratulations };
    return { level: uiTexts.results.performance.beginner, emoji: "🌱💪", message: uiTexts.results.congratulations };
  };

  const generateShareableResult = (): { text: string; hashtags: string[] } => {
    const percentage = getScorePercentage();
    const correctCount = getCorrectAnswersCount();
    const passed = isPassed();
    const emoji = passed ? '🎉' : '💪';
    const status = passed ? 'PASSED' : 'READY TO RETRY';
    
    return {
      text: `${emoji} Just completed the ISTQB AI Tester Demo Quiz!\n\n📊 Score: ${percentage}% (${correctCount}/${demoQuestions.length} correct)\n🎯 Status: ${status}\n\n${passed ? '✅ Demo mastered!' : '🔥 Learning in progress!'}\n\n#ISTQB #AITesting #Ainastasia #AIQuiz`,
      hashtags: ['ISTQB', 'AITesting', 'Certification', 'TechSkills', 'QualityAssurance']
    };
  };

  const shareToLinkedIn = async (): Promise<void> => {
    const result = generateShareableResult();
    const shareText = result.text;
    const shareUrl = window.location.href; // Current demo URL
    const fullShareText = `${shareText}\n\n🎯 Try the free demo yourself: ${shareUrl}`;
    
    // Function to detect mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Create LinkedIn share URL with pre-filled text
    const createLinkedInShareUrl = (text: string): string => {
      const encodedText = encodeURIComponent(text);
      // Use the new LinkedIn sharing format that works well on both web and mobile
      return `https://www.linkedin.com/feed/?shareActive=true&text=${encodedText}`;
    };
    
    const linkedInShareUrl = createLinkedInShareUrl(fullShareText);
    
    // Try Web Share API first (modern browsers, especially mobile)
    if (navigator.share && isMobile) {
      try {
        await navigator.share({
          title: 'ISTQB AI Tester Demo Quiz Result',
          text: shareText,
          url: shareUrl
        });
        return;
      } catch (error) {
        // If user cancels or error occurs, fall back to LinkedIn URL
        console.log('Web Share API cancelled or failed, opening LinkedIn...');
      }
    }
    
    // Primary approach: Copy to clipboard AND open LinkedIn simultaneously
    try {
      // Copy to clipboard
      await navigator.clipboard.writeText(fullShareText);
      
      // Open LinkedIn with pre-filled text
      const linkedInWindow = window.open(linkedInShareUrl, '_blank');
      
      // Show success message with additional instructions
      if (linkedInWindow) {
        // Give a moment for the window to open, then show the message
        setTimeout(() => {
          alert('🎉 Great! We\'ve opened LinkedIn for you and copied your demo result to clipboard!\n\n✅ LinkedIn should open with your text pre-filled\n✅ If the text isn\'t there, just paste it (Ctrl+V)\n✅ The demo link is included for others to try!\n\nShare your AI testing knowledge! 🚀');
        }, 500);
      } else {
        // Pop-up blocked or failed to open
        alert('🎉 Demo result copied to clipboard!\n\n⚠️ Please allow pop-ups to auto-open LinkedIn\n\nManual steps:\n1. Visit: linkedin.com/feed\n2. Click "Start a post"\n3. Paste your result (Ctrl+V)\n\nThe demo link is included! 🚀');
      }
    } catch (clipboardError) {
      // Clipboard failed, but still try to open LinkedIn
      const linkedInWindow = window.open(linkedInShareUrl, '_blank');
      
      if (linkedInWindow) {
        // LinkedIn opened successfully
        setTimeout(() => {
          alert('🎉 LinkedIn opened with your demo result!\n\n✅ Your text should be pre-filled\n✅ If not, here\'s your result to copy:\n\n' + fullShareText + '\n\nShare your AI testing journey! 🚀');
        }, 500);
      } else {
        // Both clipboard and LinkedIn opening failed
        const manualUrl = 'https://www.linkedin.com/feed/';
        window.open(manualUrl, '_blank');
        
        // Show the text in a prompt so user can copy it manually
        prompt('Copy this text to share on LinkedIn:\n\n(LinkedIn should also open in a new tab)', fullShareText);
      }
    }
  };



  if (currentView === 'welcome') {
    return (
      <div className="min-h-screen bg-black text-white overflow-x-hidden">
        {/* Dynamic background */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-900/20 to-black"></div>
        
        {/* Floating particles */}
        <div className="absolute inset-0 opacity-30">
          {[...Array(window.innerWidth < 768 ? 15 : 25)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-2 h-2 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full ${animateOnlyDesktop('animate-pulse')}`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-area-inset">
          <div className="max-w-6xl mx-auto text-center">
            
            {/* Language Selector - Fixed position - Always visible on welcome screen */}
            <div className="fixed top-4 right-4 z-50">
              <LanguageSelector 
                currentLocale={currentLocale}
                onLocaleChange={changeLocale}
              />
            </div>
            
            {/* Pull to refresh hint for mobile */}
            <div className="block sm:hidden text-center text-xs text-gray-500 mb-4">
              ↓ Pull down to refresh ↓
            </div>
            
            {/* Free Demo Badge */}
            <div className={`mb-6 sm:mb-8 ${animateOnlyDesktop('animate-[fadeInUp_0.8s_ease-out]')}`}>
              <div className="inline-flex items-center bg-gradient-to-r from-green-400/20 to-emerald-400/20 border-2 border-green-400/40 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-4 sm:mb-6">
                <Gift className="mr-2 text-green-400" size={20} />
                <span className="text-green-400 font-bold text-sm sm:text-lg">{uiTexts.demo.badge}</span>
                <Sparkles className="ml-2 text-green-400" size={16} />
              </div>
            </div>
            
            {/* Header */}
            <div className="mb-8 sm:mb-12 animate-[fadeInUp_0.8s_ease-out]">
              <div className="relative">
                <Zap className={`absolute -top-4 sm:-top-8 -left-4 sm:-left-8 text-yellow-400 ${animateOnlyDesktop('animate-bounce')}`} size={24} />
                <h1 className="text-5xl sm:text-7xl md:text-9xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-400 bg-clip-text text-transparent leading-tight">
                  {uiTexts.demo.title}
                </h1>
                <Sparkles className="absolute -top-3 sm:-top-6 -right-5 sm:-right-10 text-red-400 animate-pulse" size={20} />
              </div>
              
              <div className="relative">
                <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                  {uiTexts.demo.subtitle}
                </h2>
                <p className="text-lg sm:text-xl md:text-2xl text-gray-300 font-light mb-6 sm:mb-8 px-4 sm:px-0">
                  {uiTexts.demo.description}
                </p>
              </div>
            </div>

            {/* Demo Version Features */}
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-2 border-green-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-8 sm:mb-12 animate-[fadeInUp_0.8s_ease-out_0.2s_both]">
              <div className="flex items-center justify-center mb-6 sm:mb-8">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full flex items-center justify-center mr-4">
                  <Gift className="text-white" size={32} />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-green-400">{uiTexts.demo.comparison.demo.title}</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {uiTexts.demo.comparison.demo.features.map((feature, index) => {
                  const icons = [Brain, Clock, CheckCircle, Trophy];
                  const colors = ['green', 'emerald', 'cyan', 'teal'];
                  const Icon = icons[index % icons.length];
                  const color = colors[index % colors.length];
                  
                  return (
                    <div key={index} className="flex flex-col items-center text-center bg-black/30 rounded-xl p-4 sm:p-6 hover:bg-black/40 transition-all duration-300">
                      <Icon className={`text-${color}-400 mb-3`} size={28} />
                      <span className="text-white font-semibold text-sm sm:text-base">{feature}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Full Version Features */}
            <div className="bg-gray-900/50 border border-gray-700/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-8 sm:mb-12 animate-[fadeInUp_0.8s_ease-out_0.4s_both]">
              <h3 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                🎯 {uiTexts.demo.features.title}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {uiTexts.demo.features.items.map((feature, index) => {
                  const icons = [Brain, CheckCircle, Clock, Trophy, TrendingUp, RotateCcw, Crown, Award, Gift];
                  const colors = [
                    'from-blue-500 to-cyan-500 text-blue-400',
                    'from-green-500 to-emerald-500 text-green-400', 
                    'from-purple-500 to-pink-500 text-purple-400',
                    'from-yellow-500 to-orange-500 text-yellow-400',
                    'from-red-500 to-rose-500 text-red-400',
                    'from-indigo-500 to-blue-500 text-indigo-400',
                    'from-pink-500 to-violet-500 text-pink-400',
                    'from-emerald-500 to-teal-500 text-emerald-400',
                    'from-orange-500 to-amber-500 text-orange-400'
                  ];
                  const IconComponent = icons[index % icons.length];
                  const colorClass = colors[index % colors.length];
                  
                  return (
                    <div key={index} className="flex items-center p-4 bg-gray-800/50 rounded-xl hover:bg-gray-800/70 transition-all duration-300">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r ${colorClass.split(' ').slice(0, 2).join(' ')} rounded-lg flex items-center justify-center mr-4 flex-shrink-0`}>
                        <IconComponent className="text-white" size={20} />
                      </div>
                      <span className={`font-semibold text-sm sm:text-base ${colorClass.split(' ').slice(2).join(' ')}`}>
                        {feature}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Perfect For Section */}
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-2 border-blue-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-8 sm:mb-12 animate-[fadeInUp_0.8s_ease-out_0.6s_both]">
              <h3 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent text-center">
                🎯 {uiTexts.demo.perfectFor.title}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {uiTexts.demo.perfectFor.items.map((item, index) => {
                  const icons = [Users, GraduationCap, Award, Briefcase, Rocket, Trophy];
                  const colors = ['blue', 'purple', 'green', 'orange', 'pink', 'yellow'];
                  const Icon = icons[index % icons.length];
                  const color = colors[index % colors.length];
                  
                  return (
                    <div key={index} className="flex items-center bg-black/30 rounded-xl p-4 sm:p-6">
                      <Icon className={`text-${color}-400 mr-3 sm:mr-4 flex-shrink-0`} size={24} />
                      <span className="text-white text-sm sm:text-base">{item}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA Section */}
            <div className="animate-[fadeInUp_0.8s_ease-out_0.8s_both] px-4 sm:px-0">
              <button
                onClick={startQuiz}
                className="group relative inline-flex items-center px-8 sm:px-16 py-4 sm:py-6 text-xl sm:text-2xl md:text-3xl font-bold text-white bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 rounded-2xl sm:rounded-3xl hover:from-yellow-300 hover:via-pink-400 hover:to-purple-400 transform hover:scale-105 transition-all duration-300 shadow-2xl shadow-pink-500/30 w-full sm:w-auto"
              >
                <Rocket className="mr-3 sm:mr-4 group-hover:animate-bounce" size={28} />
                {uiTexts.demo.startButton}
                <Sparkles className="ml-3 sm:ml-4 group-hover:animate-spin" size={28} />
                
                {/* Animated glow */}
                <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 opacity-0 group-hover:opacity-50 blur-xl transition-opacity duration-300 -z-10"></div>
              </button>
              
              <p className="text-gray-400 mt-4 sm:mt-6 text-base sm:text-xl font-light">
                🚀 <strong className="text-white">{uiTexts.demo.community}</strong>
              </p>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-12 opacity-70 animate-[fadeInUp_0.8s_ease-out_0.8s_both]">
              <div className="flex items-center">
                <Award className="mr-2 sm:mr-3 text-purple-400" size={20} />
                <span className="text-base sm:text-lg">Based on ISTQB official materials</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const percentage = getScorePercentage();
    const passed = isPassed();
    const performance = getPerformanceLevel();
    
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-900/20 to-black"></div>
        
        {/* Language Selector - Fixed position - Hidden during quiz and results */}
        {!quizStarted && !showResults && (
          <div className="fixed top-4 right-4 z-50">
            <LanguageSelector 
              currentLocale={currentLocale}
              onLocaleChange={changeLocale}
            />
          </div>
        )}
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="max-w-5xl mx-auto">
            
            {/* Results Header */}
            <div className="text-center mb-8 sm:mb-12 animate-[fadeInUp_0.8s_ease-out]">
              <div className="inline-flex items-center bg-gradient-to-r from-green-400/20 to-emerald-400/20 border-2 border-green-400/40 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-4 sm:mb-6">
                <Trophy className="mr-2 text-green-400" size={20} />
                <span className="text-green-400 font-bold text-sm sm:text-lg">{uiTexts.results.title}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                {performance.emoji}
              </h1>
              
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                {performance.level}
              </h2>
            </div>

            {/* Score Display */}
            <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-12 mb-6 sm:mb-8 text-center animate-[fadeInUp_0.8s_ease-out_0.2s_both]">
              <div className="text-6xl sm:text-7xl md:text-9xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                {percentage}%
              </div>
              
              <div className="text-lg sm:text-xl md:text-3xl font-bold text-white mb-4 sm:mb-6 px-2 sm:px-0">
                {performance.message}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 max-w-2xl mx-auto">
                <div className="bg-black/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className="text-2xl sm:text-3xl font-bold text-yellow-400 mb-1 sm:mb-2">{getCorrectAnswersCount()}</div>
                  <div className="text-gray-300 text-xs sm:text-sm">{uiTexts.results.statistics.correct}</div>
                </div>
                <div className="bg-black/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className="text-2xl sm:text-3xl font-bold text-purple-400 mb-1 sm:mb-2">{demoQuestions.length}</div>
                  <div className="text-gray-300 text-xs sm:text-sm">{uiTexts.common.questions}</div>
                </div>
                <div className="bg-black/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className="text-2xl sm:text-3xl font-bold text-pink-400 mb-1 sm:mb-2">{maxStreak}</div>
                  <div className="text-gray-300 text-xs sm:text-sm">{uiTexts.results.statistics.bestStreak}</div>
                </div>
                <div className="bg-black/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className={`${getAdaptiveFontSize(passed ? uiTexts.results.status.pass : uiTexts.results.status.study)} font-bold text-green-400 mb-1 sm:mb-2 leading-tight whitespace-nowrap`}>
                    {passed ? uiTexts.results.status.pass : uiTexts.results.status.study}
                  </div>
                  <div className="text-gray-300 text-xs sm:text-sm">{uiTexts.results.statistics.status}</div>
                </div>
              </div>
            </div>

            {/* Upgrade CTA */}
            <div className="bg-gradient-to-r from-yellow-400/10 to-orange-400/10 border-2 border-yellow-400/40 rounded-2xl sm:rounded-3xl p-6 sm:p-10 mb-6 sm:mb-8 text-center animate-[fadeInUp_0.8s_ease-out_0.4s_both]">
              <div className="flex items-center justify-center mb-4 sm:mb-6">
                <Crown className="text-yellow-400 mr-2 sm:mr-3" size={32} />
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  {uiTexts.results.upgrade.title}
                </h3>
                <Crown className="text-yellow-400 ml-2 sm:ml-3" size={32} />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                {uiTexts.results.upgrade.features.slice(0, 3).map((feature, index) => (
                  <div key={index} className="bg-black/30 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                    <div className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2 ${
                      index === 0 ? 'text-yellow-400' : 
                      index === 1 ? 'text-pink-400' : 'text-purple-400'
                    }`}>
                      {index === 0 ? '300+' : index === 1 ? '11' : '6h'}
                    </div>
                    <div className="text-gray-300 text-sm sm:text-base">{feature}</div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center">
                <button 
                  onClick={onBuyFullVersion}
                  className="group relative inline-flex items-center justify-center px-6 sm:px-10 py-4 sm:py-4 text-lg sm:text-xl font-bold text-white bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-xl sm:rounded-2xl hover:from-yellow-300 hover:via-orange-400 hover:to-red-400 transform hover:scale-105 transition-all duration-300 shadow-2xl shadow-orange-500/30 w-full sm:w-auto">
                  <Lock className="mr-2 sm:mr-3 group-hover:animate-pulse" size={20} />
                  {uiTexts.results.upgrade.button}
                  <Rocket className="ml-2 sm:ml-3 group-hover:animate-bounce" size={20} />
                </button>
              </div>
            </div>


            {/* Share Section */}
            <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border-2 border-pink-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out_0.6s_both]">
              <h3 className="text-xl sm:text-2xl font-bold text-center mb-4 sm:mb-6 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                {uiTexts.results.sharing.title} 🎉
              </h3>
              <div className="flex justify-center">
                <button 
                  onClick={shareToLinkedIn}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg shadow-blue-500/25 w-full sm:w-auto"
                >
                  <div className="flex items-center justify-center">
                    <Share2 className="mr-3" size={24} />
                    {uiTexts.results.actions.shareLinkedIn}
                  </div>
                </button>
              </div>
              <p className="text-center text-gray-400 text-sm mt-3">
                💡 {uiTexts.results.sharing.description}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out_0.8s_both]">
              <button
                onClick={() => setShowDetailedResults(!showDetailedResults)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
              >
                {showDetailedResults ? uiTexts.results.actions.hideReview : uiTexts.results.actions.showReview}
              </button>
              <button
                onClick={restartQuiz}
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold py-3 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
              >
                <div className="flex items-center justify-center">
                  <RotateCcw className="mr-2" size={20} />
                  {uiTexts.results.actions.tryAgain}
                </div>
              </button>
            </div>

            {/* 🎛️ Advanced Report Feature - Demo Version (Shows Upgrade Modal) */}
            {isFeatureEnabled('ADVANCED_REPORT_ENABLED') && showDetailedResults && (
              <div className="mb-8 animate-[fadeInUp_0.8s_ease-out_0.5s_both]">
                <div className="relative group">
                  {/* Pulsing glow effect */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
                  
                  <button
                    onClick={showFullReviewUpgrade}
                    className="relative w-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 hover:from-yellow-300 hover:via-pink-400 hover:to-purple-500 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105"
                  >
                    <div className="flex items-center justify-center">
                      <Brain className="mr-3 animate-pulse" size={24} />
                      <span className="text-lg font-extrabold">🚀 GET FULL REVIEW</span>
                      <Sparkles className="ml-3 animate-bounce" size={24} />
                    </div>
                    
                    <div className="text-sm mt-1 opacity-90">
                      Мега-интерактивный анализ с 3D архетипом и космическими инсайтами! 
                    </div>
                  </button>
                </div>
                
                {/* Benefits list */}
                <div className="mt-4 text-center">
                  <div className="inline-flex flex-wrap gap-2 justify-center">
                    <span className="bg-yellow-400/20 text-yellow-300 px-3 py-1 rounded-full text-xs font-medium">🎭 3D Архетип</span>
                    <span className="bg-pink-400/20 text-pink-300 px-3 py-1 rounded-full text-xs font-medium">🌌 Космическая карта</span>
                    <span className="bg-purple-400/20 text-purple-300 px-3 py-1 rounded-full text-xs font-medium">💡 Персональные советы</span>
                    <span className="bg-blue-400/20 text-blue-300 px-3 py-1 rounded-full text-xs font-medium">📊 Интерактивные графики</span>
                    <span className="bg-red-400/20 text-red-300 px-3 py-1 rounded-full text-xs font-medium">🔒 Только в полной версии</span>
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Results - Right after action buttons */}
            {showDetailedResults && (
              <div className="bg-gray-900/50 border border-gray-700/50 rounded-2xl sm:rounded-3xl p-4 sm:p-8 mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out]">
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                  📚 {uiTexts.review.title}
                </h3>
                <div className="space-y-4 sm:space-y-6 max-h-96 overflow-y-auto">
                  {demoQuestions.map((question, index) => {
                    const userAnswer = selectedAnswers[question.id];
                    const isCorrect = userAnswer === question.correct;
                    
                    return (
                      <div key={question.id} className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl border-2 ${isCorrect ? 'border-green-500/40 bg-green-900/10' : 'border-red-500/40 bg-red-900/10'}`}>
                        <div className="flex items-start justify-between mb-3 sm:mb-4">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                              {index + 1}
                            </span>
                            <span className="text-xs sm:text-sm font-semibold text-gray-400">
                              {question.chapter} • {question.difficulty}
                            </span>
                          </div>
                          {isCorrect ? (
                            <CheckCircle className="text-green-400" size={20} />
                          ) : (
                            <XCircle className="text-red-400" size={20} />
                          )}
                        </div>
                        <p className="text-white mb-3 sm:mb-4 text-base sm:text-lg">{question.text}</p>
                        <div className="space-y-2">
                          <p className={`text-sm sm:text-base ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                            <span className="font-semibold">{uiTexts.review.yourAnswer}</span> {userAnswer !== undefined ? question.options[userAnswer] : uiTexts.review.notAnswered}
                          </p>
                          {!isCorrect && (
                            <p className="text-green-400 text-sm sm:text-base">
                              <span className="font-semibold">{uiTexts.review.correctAnswer}</span> {question.options[question.correct]}
                            </p>
                          )}
                          <div className="bg-blue-900/20 border border-blue-500/30 p-3 sm:p-4 rounded-xl mt-3 sm:mt-4">
                            <p className="text-blue-300 text-sm sm:text-base">
                              <span className="font-semibold">💡 {uiTexts.review.explanation}</span> {userAnswer !== undefined ? getExplanationForAnswer(question, userAnswer) : question.expA}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Course Promotion */}
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-2 border-green-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out_0.9s_both]">
              <div className="text-center mb-4 sm:mb-6">
                <div className="inline-flex items-center bg-green-400/20 border border-green-400/40 rounded-full px-4 py-2 mb-4">
                  <span className="text-green-400 font-bold text-sm sm:text-base">{uiTexts.results.course.subtitle}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-3">
                  {uiTexts.results.course.title}
                </h3>
                <p className="text-gray-300 text-sm sm:text-base mb-4 max-w-2xl mx-auto">
                  {uiTexts.results.course.description}
                </p>
              </div>
              
              {uiTexts.results.course.features.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
                  {uiTexts.results.course.features.map((feature, index) => (
                    <div key={index} className="bg-black/30 rounded-xl p-4 text-center">
                      <div className={`text-2xl font-bold mb-2 ${
                        index === 0 ? 'text-green-400' : 
                        index === 1 ? 'text-emerald-400' : 'text-green-400'
                      }`}>
                        {index === 0 ? '🐌 🚶 🏃' : index === 1 ? '🎯' : '15%'}
                      </div>
                      <div className="text-gray-300 text-sm">{feature}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="text-center">
                <button 
                  onClick={() => window.open('https://ainastasia.mykajabi.com/pl/2148634864', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes')}
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-4 text-lg sm:text-xl font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl hover:from-green-600 hover:to-emerald-700 transform hover:scale-105 transition-all duration-300 shadow-2xl shadow-green-500/30 w-full sm:w-auto cursor-pointer"
                >
                  <Star className="mr-2 sm:mr-3" size={20} />
                  {uiTexts.results.course.button}
                  <ChevronRight className="ml-2 sm:ml-3" size={20} />
                </button>
              </div>
            </div>

            {/* Feedback Section */}
            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-2 border-blue-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-6 sm:mb-8 animate-[fadeInUp_0.8s_ease-out_0.9s_both]">
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">
                  {uiTexts.results.feedback.title}
                </h3>
                
                <a 
                  href="https://form.typeform.com/to/Qyxa1kQu" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-bold py-3 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-blue-500/25"
                >
                  <Brain className="mr-2" size={20} />
                  {uiTexts.results.feedback.button}
                </a>
              </div>
            </div>

            {/* Social Media Section */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-2 border-purple-500/40 rounded-2xl sm:rounded-3xl p-6 sm:p-8 animate-[fadeInUp_0.8s_ease-out_1s_both]">
              <div className="text-center mb-6">
                <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-3">
                  {uiTexts.results.social.title}
                </h3>
                <p className="text-gray-300 text-sm sm:text-base">
                  {uiTexts.results.social.description}
                </p>
              </div>
              
              <div className="flex justify-center gap-4 sm:gap-6">
                <a 
                  href="https://instagram.com/ai.nastasia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center hover:transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-pink-500/30"
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                
                <a 
                  href="https://tiktok.com/@ai.nastasia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-pink-500 to-red-500 flex items-center justify-center hover:transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-red-500/30"
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                </a>
                
                <a 
                  href="https://www.linkedin.com/in/ainastasia/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-center hover:transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-blue-500/30"
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                
                <a 
                  href="https://t.me/ai_nastasia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center hover:transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-blue-500/30"
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </a>
                
                <a 
                  href="https://www.youtube.com/@ainastasiia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center hover:transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-red-500/30"
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a2.93 2.93 0 0 0-2.063-2.063C19.611 3.55 12 3.55 12 3.55s-7.611 0-9.435.573A2.93 2.93 0 0 0 .502 6.186C0 8.01 0 12 0 12s0 3.99.502 5.814a2.93 2.93 0 0 0 2.063 2.063C4.389 20.45 12 20.45 12 20.45s7.611 0 9.435-.573a2.93 2.93 0 0 0 2.063-2.063C24 15.99 24 12 24 12s0-3.99-.502-5.814zM9.546 15.569V8.431L15.818 12l-6.272 3.569z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Function to get adaptive font size based on text length
  function getAdaptiveFontSize(text: string) {
    if (text.length <= 6) return "text-lg sm:text-xl lg:text-2xl xl:text-3xl";
    if (text.length <= 10) return "text-base sm:text-lg lg:text-xl xl:text-2xl";
    if (text.length <= 15) return "text-sm sm:text-base lg:text-lg xl:text-xl";
    return "text-xs sm:text-sm lg:text-base xl:text-lg";
  }

  const currentQ = demoQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / demoQuestions.length) * 100;

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-900/20 to-black"></div>
      
      {/* Mobile Language Selector - Top right - Hidden during quiz and results */}
      {!quizStarted && !showResults && (
        <div className="block sm:hidden fixed top-2 right-2 z-50">
          <LanguageSelector 
            currentLocale={currentLocale}
            onLocaleChange={changeLocale}
            isMobile={true}
          />
        </div>
      )}
      
      <div className="relative z-10 container mx-auto px-3 sm:px-6 pt-4 pb-2 sm:py-6 safe-area-inset">
        
        {/* Mobile status bar spacing */}
        <div className="h-safe-top sm:h-0"></div>
        
        {/* Ultra Compact Header for Mobile */}
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-2 border-purple-500/40 rounded-lg sm:rounded-xl p-2 sm:p-4 mb-2 sm:mb-6">
          {/* Mobile Layout */}
          <div className="block sm:hidden">
            {/* Mobile Header Content */}
            <div className="flex items-center justify-between mb-3 pr-12">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  {uiTexts.quiz.header.title}
                </h1>
                <span className="text-gray-400 text-sm">
                  {currentQuestion + 1}/{demoQuestions.length}
                </span>
              </div>
              <div className="bg-black/80 backdrop-blur-sm rounded-lg px-3 py-2 border border-yellow-400/40">
                <div className="flex items-center text-white">
                  <Clock className="mr-2 text-yellow-400" size={16} />
                  <span className="text-sm font-mono text-yellow-400">
                    {formatTime(timeLeft)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Ultra Compact Progress Bar for Mobile */}
            <div className="relative">
              <div className="bg-gray-700 rounded-full h-1.5">
                <div 
                  className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-start text-xs text-gray-400 mt-1">
                <span>{Math.round(progress)}%</span>
              </div>
            </div>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden sm:block">
            {/* Desktop Header and Timer - Same level */}
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  {uiTexts.quiz.header.title}
                </h1>
                <span className="text-gray-400 text-sm">
                  {currentQuestion + 1}/{demoQuestions.length}
                </span>
              </div>
              <div className="flex items-center text-white bg-yellow-400/20 px-3 py-1.5 rounded-lg border border-yellow-400/40">
                <Clock className="mr-2 text-yellow-400" size={16} />
                <span className="text-base font-mono text-yellow-400">
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>
            
            {/* Desktop Progress Bar */}
            <div className="relative mt-2">
              <div className="bg-gray-700 rounded-full h-1.5">
                <div 
                  className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-start text-xs text-gray-400 mt-1">
                <span>{Math.round(progress)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Language Selector - Far outside block - Hidden during quiz and results */}
        {!quizStarted && !showResults && (
          <div className="hidden sm:block fixed top-4 right-4 z-50">
            <LanguageSelector 
              currentLocale={currentLocale}
              onLocaleChange={changeLocale}
            />
          </div>
        )}

        {/* Chapter/Topic - Center below progress bar */}
        {currentQ && (
          <div className="text-center mb-4">
            <div className="inline-block bg-gray-800/60 backdrop-blur-sm px-4 py-2 rounded-lg border border-gray-600/40">
              <div className="text-sm text-gray-300 font-medium">
                {currentQ.chapter}
              </div>
            </div>
          </div>
        )}

        {/* Question */}
        {currentQ && (
          <div 
            className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-2 border-gray-600/50 rounded-lg sm:rounded-xl p-2 sm:p-4 mb-2 sm:mb-6 animate-[fadeInUp_0.6s_ease-out] relative"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Swipe indicator for mobile */}
            <div className="block sm:hidden absolute top-1 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gray-600 rounded-full"></div>
            
            {/* Mobile Question Header */}
            <div className="block sm:hidden">
              <div className="flex justify-between items-center mb-2 pt-2">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold ${currentQ.difficulty === 'easy' ? 'bg-green-500' : 'bg-yellow-500'}`}>
                    {currentQuestion + 1}
                  </span>
                  <h2 className="text-sm font-bold text-white">
                    {currentQ.difficulty === 'easy' ? '🟢 Easy' : 
                     currentQ.difficulty === 'medium' ? '🟡 Medium' : 
                     currentQ.difficulty === 'K1' ? `🟢 ${uiTexts.common.difficulty.k1}` : 
                     currentQ.difficulty === 'K2' ? `🟡 ${uiTexts.common.difficulty.k2}` : 
                     currentQ.difficulty === 'K3' ? `🟠 ${uiTexts.common.difficulty.k3}` : `🔴 ${uiTexts.common.difficulty.k4}`}
                  </h2>
                </div>
                <div className="text-lg">💭</div>
              </div>
              
              <p className="text-white text-base leading-snug mb-3">
                {currentQ.text}
              </p>

              {/* Mobile Options */}
              <div className="space-y-2">
                {currentQ.options.map((option: string, index: number) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(currentQ.id, index)}
                    className={`w-full text-left p-2.5 rounded-lg border-2 transition-all duration-300 transform min-h-[44px] touch-manipulation active:scale-[0.98] ${
                      selectedAnswers[currentQ.id] === index
                        ? 'border-pink-500 bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-white shadow-lg shadow-pink-500/25'
                        : 'border-gray-600 bg-gray-800/50 text-gray-300 hover:border-pink-400/50 hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-start">
                      <span className={`font-bold mr-2.5 text-sm min-w-[1.25rem] h-5 w-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                        selectedAnswers[currentQ.id] === index 
                          ? 'bg-pink-500 text-white' 
                          : 'bg-gray-600 text-gray-300'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="text-base leading-snug">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Swipe hint for mobile */}
              <div className="text-center text-xs text-gray-500 mt-2 mb-4">
                👈 Swipe left/right to navigate 👉
              </div>
              
              {/* Question Overview for Mobile */}
              <div className="bg-gray-900/30 border border-gray-700/30 rounded-lg p-2 mb-4">
                <h3 className="text-white font-bold mb-2 flex items-center text-xs">
                  <Menu className="mr-1 text-yellow-400" size={14} />
                  Questions
                </h3>
                <div className="grid grid-cols-6 gap-1.5">
                  {demoQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentQuestionWithFeedback(index)}
                      className={`aspect-square rounded-md text-xs font-bold transition-all duration-300 transform active:scale-95 min-h-[28px] ${
                        index === currentQuestion
                          ? 'bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 text-white shadow-lg'
                          : selectedAnswers[question.id] !== undefined
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-700 text-gray-300'
                      }`}
                      title={`Question ${index + 1} - ${question.difficulty}${selectedAnswers[question.id] !== undefined ? ' (Answered)' : ' (Not answered)'}`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Desktop Question Layout */}
            <div className="hidden sm:block">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-sm sm:text-base font-bold ${currentQ.difficulty === 'easy' ? 'bg-green-500' : 'bg-yellow-500'}`}>
                    {currentQuestion + 1}
                  </span>
                  <h2 className="text-base sm:text-lg font-bold text-white">
                    {currentQ.difficulty === 'easy' ? '🟢 Easy' : 
                     currentQ.difficulty === 'medium' ? '🟡 Medium' : 
                     currentQ.difficulty === 'K1' ? `🟢 ${uiTexts.common.difficulty.k1}` : 
                     currentQ.difficulty === 'K2' ? `🟡 ${uiTexts.common.difficulty.k2}` : 
                     currentQ.difficulty === 'K3' ? `🟠 ${uiTexts.common.difficulty.k3}` : `🔴 ${uiTexts.common.difficulty.k4}`}
                  </h2>
                </div>
                <div className="text-xl sm:text-2xl">💭</div>
              </div>
              
              <p className="text-white text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                {currentQ.text}
              </p>

              {/* Desktop Options */}
              <div className="space-y-2 sm:space-y-3">
                {currentQ.options.map((option: string, index: number) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(currentQ.id, index)}
                    className={`w-full text-left p-3 sm:p-4 rounded-lg border-2 transition-all duration-300 transform min-h-[50px] sm:min-h-[60px] touch-manipulation hover:scale-[1.01] active:scale-[0.99] ${
                      selectedAnswers[currentQ.id] === index
                        ? 'border-pink-500 bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-white shadow-lg shadow-pink-500/25'
                        : 'border-gray-600 bg-gray-800/50 text-gray-300 hover:border-pink-400/50 hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-start">
                      <span className={`font-bold mr-3 text-base sm:text-lg min-w-[1.5rem] sm:min-w-[2rem] h-6 w-6 sm:h-8 sm:w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        selectedAnswers[currentQ.id] === index 
                          ? 'bg-pink-500 text-white' 
                          : 'bg-gray-600 text-gray-300'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="text-base sm:text-lg leading-relaxed">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Navigation - Fixed on mobile with safe area */}
        <div className="fixed sm:static bottom-0 left-0 right-0 bg-black/95 sm:bg-transparent backdrop-blur-sm sm:backdrop-blur-none border-t sm:border-t-0 border-gray-700/50 p-3 sm:p-0 sm:mb-4 z-50 pb-safe-bottom">
          <div className="flex justify-between items-center gap-3 max-w-lg sm:max-w-none mx-auto">
            <button
              onClick={handlePrevQuestion}
              disabled={currentQuestion === 0}
              className="flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50 text-white rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 min-w-[60px] sm:min-w-[80px] min-h-[40px] touch-manipulation"
            >
              <ArrowLeft className="mr-1 sm:mr-2" size={16} />
              <span className="text-sm sm:text-base">{uiTexts.quiz.navigation.previous}</span>
            </button>

            <div className="text-center flex-1 max-w-[100px] sm:max-w-none">
              <div className="text-white text-sm sm:text-base font-medium">
                {Object.keys(selectedAnswers).length}/{demoQuestions.length}
              </div>
              <div className="text-gray-400 text-xs sm:text-sm">
                {uiTexts.quiz.status.current}
              </div>
            </div>

            {currentQuestion === demoQuestions.length - 1 ? (
              <button
                onClick={handleFinishQuiz}
                className="flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 hover:from-yellow-300 hover:via-pink-400 hover:to-purple-400 text-white rounded-lg transition-all duration-300 font-bold transform hover:scale-105 active:scale-95 shadow-lg shadow-pink-500/25 min-w-[70px] sm:min-w-[100px] min-h-[40px] touch-manipulation"
              >
                <Trophy className="mr-1 sm:mr-2" size={16} />
                <span className="text-sm sm:text-base">{uiTexts.quiz.navigation.finish}</span>
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 min-w-[60px] sm:min-w-[80px] min-h-[40px] touch-manipulation"
              >
                <span className="text-sm sm:text-base">{uiTexts.quiz.navigation.next}</span>
                <ArrowRight className="ml-1 sm:ml-2" size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Mobile spacing for fixed navigation with safe area */}
        <div className="h-20 sm:h-0"></div>

        {/* Question Overview for Desktop only */}
        <div className="hidden sm:block bg-gray-900/30 border border-gray-700/30 rounded-xl p-4">
          <h3 className="text-white font-bold mb-3 flex items-center text-base">
            <Menu className="mr-2 text-yellow-400" size={14} />
            Question Overview
          </h3>
          <div className="grid grid-cols-8 md:grid-cols-12 gap-2">
            {demoQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestionWithFeedback(index)}
                className={`aspect-square rounded-lg text-xs font-bold transition-all duration-300 transform hover:scale-110 min-h-[36px] ${
                  index === currentQuestion
                    ? 'bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 text-white shadow-lg'
                    : selectedAnswers[question.id] !== undefined
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                title={`Question ${index + 1} - ${question.difficulty}${selectedAnswers[question.id] !== undefined ? ' (Answered)' : ' (Not answered)'}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Upgrade Modal for Full Review */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 sm:p-8 max-w-md w-full mx-4 relative overflow-hidden">
            {/* Cosmic background effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-pink-600/20 to-yellow-600/20 opacity-50"></div>
            
            <div className="relative z-10">
              {/* Close button */}
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors"
              >
                <XCircle size={24} />
              </button>

              {/* Header */}
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">🚀</div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 bg-clip-text text-transparent mb-2">
                  Полный анализ доступен только в PRO!
                </h2>
                <p className="text-gray-300 text-sm">
                  Хочешь увидеть свой 3D архетип и получить космические инсайты?
                </p>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center">
                  <div className="text-2xl mr-3">🎭</div>
                  <div>
                    <div className="font-semibold text-white">3D Интерактивный архетип</div>
                    <div className="text-sm text-gray-400">Анимированный аватар твоего типа тестировщика</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="text-2xl mr-3">🌌</div>
                  <div>
                    <div className="font-semibold text-white">Космическая карта знаний</div>
                    <div className="text-sm text-gray-400">3D галактика твоих навыков с интерактивными планетами</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="text-2xl mr-3">💡</div>
                  <div>
                    <div className="font-semibold text-white">Персональные инсайты</div>
                    <div className="text-sm text-gray-400">Детальный план развития и рекомендации</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="text-2xl mr-3">📊</div>
                  <div>
                    <div className="font-semibold text-white">Интерактивные графики</div>
                    <div className="text-sm text-gray-400">Анимированная визуализация твоего прогресса</div>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    onBuyFullVersion?.();
                  }}
                  className="w-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 hover:from-yellow-300 hover:via-pink-400 hover:to-purple-500 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  🎯 Получить полную версию
                </button>
                
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-300"
                >
                  Пока не нужно
                </button>
              </div>

              {/* Note */}
              <p className="text-xs text-gray-500 text-center mt-4">
                В полной версии 40 вопросов + мега-отчет с 3D анализом! 🌟
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ISTQBAIDemoQuiz;